import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Profile } from "@/lib/types";
import { useDemoMode } from "./use-demo-mode";

interface AuthCtx {
  loading: boolean;
  userId: string | null;
  profile: Profile | null;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  isSettingUpProfile: boolean;
}

const Ctx = createContext<AuthCtx>({
  loading: true,
  userId: null,
  profile: null,
  signOut: async () => {},
  refresh: async () => {},
  isSettingUpProfile: false,
});

const demoStaffProfile: Profile = {
  id: "demo-staff",
  email: "demo@resolute.print",
  full_name: "Demo Staff",
  role: "staff",
  customer_id: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const { demoMode } = useDemoMode();
  const [userId, setUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSettingUpProfile, setIsSettingUpProfile] = useState(false);

  const loadProfile = async (uid: string) => {
    const { data } = await supabase.from("profiles").select("*").eq("id", uid).maybeSingle();
    const profileData = (data as Profile | null) ?? null;
    setProfile(profileData);
    setIsSettingUpProfile(false);
  };

  const refresh = async () => {
    if (!userId) return;
    await loadProfile(userId);
  };

  useEffect(() => {
    if (demoMode) {
      setUserId("demo-staff");
      setProfile(demoStaffProfile);
      setLoading(false);
      setIsSettingUpProfile(false);
      return;
    }

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id ?? null;
      setUserId(uid);
      if (uid) {
        setIsSettingUpProfile(true);
        setTimeout(() => loadProfile(uid), 0);
      } else {
        setProfile(null);
        setIsSettingUpProfile(false);
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data }) => {
      const uid = data.session?.user?.id ?? null;
      setUserId(uid);
      if (uid) {
        setIsSettingUpProfile(true);
        loadProfile(uid).finally(() => setLoading(false));
      } else {
        setLoading(false);
        setIsSettingUpProfile(false);
      }
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, [demoMode]);

  const signOut = async () => {
    if (!demoMode) await supabase.auth.signOut();
    setProfile(null);
    setUserId(null);
    setIsSettingUpProfile(false);
  };

  return (
    <Ctx.Provider value={{ loading, userId, profile, signOut, refresh, isSettingUpProfile }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
